import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Loader2, CalendarPlus, Mic } from "lucide-react";
import { toast } from "sonner";

const me = () => {
  try { return JSON.parse(localStorage.getItem("sf_user") || "{}")?.email || "user"; }
  catch { return "user"; }
};

// SMS the customer their pre-funding call options + assisted calendar booking
// + Aircall recording fetch (upgrade list item 4).
export const PrefundingSmsScheduler = ({ application, onChanged }) => {
  const appId = application?.id;
  const [invites, setInvites] = useState([]);
  const [smsOpen, setSmsOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [lines, setLines] = useState([]);
  const [lineId, setLineId] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [fetchingRec, setFetchingRec] = useState(false);
  const [bookDate, setBookDate] = useState(new Date().toISOString().slice(0, 10));
  const [bookTime, setBookTime] = useState("");

  const reply = application?.prefunding_sms_reply;
  const latestInvite = invites[0];

  const loadInvites = async () => {
    try {
      const r = await api.get(`/applications/${appId}/prefunding-call/sms-invites`);
      setInvites(r.data?.invites || []);
    } catch { /* non-blocking */ }
  };

  useEffect(() => { if (appId) loadInvites(); }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSms = async () => {
    setSmsOpen(true);
    setToNumber(latestInvite?.to_number || application?.customer_phone ||
      application?.loandisk_loan_data?.borrower_mobile || "");
    try {
      const r = await api.get(`/applications/${appId}/prefunding-call/sms-lines`);
      const ls = r.data?.lines || [];
      setLines(ls);
      if (ls.length) setLineId(String(ls[0].id));
    } catch (e) {
      toast.error("Aircall lines unavailable: " + (e?.response?.data?.detail || e.message));
    }
  };

  const sendSms = async () => {
    try {
      setBusy(true);
      await api.post(`/applications/${appId}/prefunding-call/sms-invite`, {
        to_number: toNumber.trim(), line_id: parseInt(lineId, 10), sent_by: me(),
      });
      toast.success("Call options SMS sent — replies will appear here and ping Slack");
      setSmsOpen(false);
      loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SMS send failed");
    } finally { setBusy(false); }
  };

  const bookCall = async () => {
    try {
      setBusy(true);
      await api.post(`/sales/tasks`, {
        title: `Pre-funding call — ${application?.application_id || ""} ${application?.company_name || ""}`.trim(),
        task_type: "callback",
        date: bookDate,
        time: bookTime,
        sf_number: application?.application_id || "",
        deal_name: application?.company_name || "",
        contact_phone: latestInvite?.to_number || "",
        notes: reply ? `Customer SMS reply (${reply.label}): ${reply.body}` : "Pre-funding call",
        assigned_to: application?.assigned_to || "",
      });
      toast.success("Pre-funding call booked into the sales calendar");
      setBookOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  const fetchRecording = async () => {
    try {
      setFetchingRec(true);
      const r = await api.post(`/applications/${appId}/prefunding-call/fetch-aircall-recording`, {
        phone: latestInvite?.to_number || "", actor: me(),
      });
      if (r.data?.status === "success") {
        toast.success(`Aircall recording attached (${Math.round((r.data.size || 0) / 1024)}KB)`);
        onChanged?.();
      } else {
        toast.info(r.data?.message || "No recording found");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recording fetch failed");
    } finally { setFetchingRec(false); }
  };

  return (
    <div className="p-3 border border-slate-200 rounded-lg bg-slate-50 space-y-2" data-testid="prefunding-sms-scheduler">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <MessageSquare className="w-4 h-4 text-blue-600" />
          Call scheduling
          {latestInvite && !reply && (
            <span className="text-[10px] bg-blue-100 text-blue-700 rounded px-1.5 py-0.5" data-testid="pf-sms-sent-badge">
              SMS sent {new Date(latestInvite.sent_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} — awaiting reply
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={openSms} data-testid="pf-sms-invite-btn">
            <MessageSquare className="w-3 h-3 mr-1" />SMS call options
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setBookOpen(true)} data-testid="pf-book-call-btn">
            <CalendarPlus className="w-3 h-3 mr-1" />Book in calendar
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={fetchRecording} disabled={fetchingRec} data-testid="pf-fetch-recording-btn"
            title="Pull the latest Aircall call recording for the customer's number and attach it to the deal">
            {fetchingRec ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mic className="w-3 h-3 mr-1" />}
            Fetch recording
          </Button>
        </div>
      </div>
      {reply && (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="pf-sms-reply-card">
          <span className="font-semibold text-emerald-800">Customer replied — {reply.label}</span>
          <span className="text-emerald-700"> · &quot;{reply.body}&quot;</span>
          <span className="text-emerald-600"> ({new Date(reply.at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })})</span>
        </div>
      )}

      {smsOpen && (
        <Dialog open onOpenChange={(o) => !o && setSmsOpen(false)}>
          <DialogContent className="max-w-md" data-testid="pf-sms-dialog">
            <DialogHeader>
              <DialogTitle>SMS pre-funding call options</DialogTitle>
              <DialogDescription>
                Texts the customer: reply 1 for ASAP, 2 for later today, or a time that suits.
                Replies land here and ping the deal&apos;s Slack thread.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Customer mobile *</Label>
                <Input value={toNumber} onChange={(e) => setToNumber(e.target.value)} placeholder="+447…"
                  data-testid="pf-sms-number" />
              </div>
              <div>
                <Label className="text-xs">Send from line *</Label>
                <Select value={lineId || undefined} onValueChange={setLineId}>
                  <SelectTrigger className="h-9" data-testid="pf-sms-line"><SelectValue placeholder="Pick line…" /></SelectTrigger>
                  <SelectContent>
                    {lines.map((l) => (
                      <SelectItem key={l.id} value={String(l.id)}>{l.name || l.digits}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" onClick={sendSms} disabled={busy || !toNumber.trim() || !lineId}
                data-testid="pf-sms-send">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                Send SMS
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {bookOpen && (
        <Dialog open onOpenChange={(o) => !o && setBookOpen(false)}>
          <DialogContent className="max-w-sm" data-testid="pf-book-dialog">
            <DialogHeader>
              <DialogTitle>Book pre-funding call</DialogTitle>
              <DialogDescription>
                Creates a callback task in the sales calendar{reply ? ` — customer asked for: ${reply.label}` : ""}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Date *</Label>
                  <Input type="date" value={bookDate} onChange={(e) => setBookDate(e.target.value)} data-testid="pf-book-date" />
                </div>
                <div>
                  <Label className="text-xs">Time</Label>
                  <Input type="time" value={bookTime} onChange={(e) => setBookTime(e.target.value)} data-testid="pf-book-time" />
                </div>
              </div>
              <Button className="w-full" onClick={bookCall} disabled={busy || !bookDate} data-testid="pf-book-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarPlus className="w-4 h-4 mr-2" />}
                Book call
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PrefundingSmsScheduler;
